import { Box, Typography, useMediaQuery } from '@mui/material'
import { useSelector } from 'react-redux'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import { motion } from 'motion/react'
import CardWithSpace from '../../../components/Cards/CardWithSpace'
import HoverTooltip from '../../../components/PageHelpers/HoverTooltip'
import { getFormattedDate } from '../../../utils/helpers'
import SeeMoreText from '../../../components/Cards/helpers/SeeMoreText'
import { selectNotification } from '../../../redux/features/notifications/notificationCenter/notificationCenterSlice'
import { cardVariants } from '../../../utils/animation'

const NotificationCenterCards = () => {
  const isXsScreen = useMediaQuery((theme) => theme.breakpoints.only('xs'))
  const notificationStore = useSelector(selectNotification)
  return (
    <Box>
      <CardWithSpace customStyles={styles.cardSpace} data-testid="notificationContainer">
        {notificationStore?.data?.map((singleNotification, index) => (
          <Box
            sx={styles.notificationCard}
            key={singleNotification?.id}
            data-testid="notification-item"
            component={motion.div}
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            custom={index}
          >
            <Box sx={styles.cardIconWithTitle}>
              <Box sx={styles.notificationIconContainer}>
                <NotificationsNoneOutlinedIcon sx={styles.notificationsNoneOutlinedIcon} />
              </Box>
              <Typography sx={styles.cardTitleHeader}>
                {singleNotification?.header}
              </Typography>
            </Box>
            {isXsScreen ? (
              <Typography sx={[styles.cardTitleHeader, styles.cardDetails]}>
                <SeeMoreText text={singleNotification?.details} />
              </Typography>
            ) : (
              <HoverTooltip
                title={singleNotification?.details}
                placement="top"
                sx={styles.description}
              >
                <Typography sx={[styles.cardTitleHeader, styles.cardDetails]}>
                  {singleNotification?.details}
                </Typography>
              </HoverTooltip>
            )}
            <Typography sx={[styles.cardTitleHeader, styles.date]}>
              {getFormattedDate(`${singleNotification?.notify_date || ''} ${singleNotification?.notify_time}`, true).split('-')[0]}
              <Typography sx={styles.secondDate}>
                {getFormattedDate(`${singleNotification?.notify_date || ''} ${singleNotification?.notify_time}`, true).split('-')[1]}
              </Typography>
            </Typography>
          </Box>
        ))}
      </CardWithSpace>
    </Box>
  )
}

const styles = {
  cardSpace: {
    marginBottom: '16px',
  },
  notificationCard: {
    boxSizing: 'border-box',
    height: { xs: 'unset', sm: '128px' },
    padding: { xs: '13px', sm: '20px 40px 40px 40px' },
    display: 'grid',
    alignItems: { xs: 'unset', sm: 'center' },
    gridTemplateColumns: { xs: '1fr', sm: '1.5fr 3.5fr 1.5fr' },
    gap: { xs: '6px', sm: '10px' },
    marginBottom: 0,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'grey.200',
  },
  cardIconWithTitle: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    height: 'fit-content',
    paddingLeft: {
      xs: '0px',
      sm: '40px',
    },
  },
  notificationIconContainer: {
    borderRadius: '10px',
    backgroundColor: '#EAFAF3',
    display: { xs: 'none', sm: 'flex' },
    alignSelf: 'flex-start',
    '& svg': {
      height: { xs: '15px', sm: '40px' },
      width: { xs: '15px', sm: '40px' },
    },
  },
  notificationsNoneOutlinedIcon: {
    fill: 'primary.main',
    fontSize: '25px !important',
    margin: '3px',
    '& path': {
      fill: '#29cc8b',
    },
  },
  cardTitleHeader: {
    color: 'text.secondary',
    fontFamily: 'Avenir',
    fontSize: '16px',
    fontWeight: 500,
  },
  cardDetails: {
    color: { xs: 'text.disabled', sm: 'inherit' },
    '& span': { fontSize: { xs: '14px !important', md: '16px !important' } },
    maxHeight: '72px',
    overflow: 'hidden',
    display: '-webkit-box',
    ' -webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    marginTop: { sm: '2px' },
    paddingLeft: {
      xs: '0px',
      sm: '40px',
    },
  },
  description: {
    display: 'flex',
    gap: '3px',
  },
  date: {
    textAlign: { xs: 'left', sm: 'center' },
    direction: 'ltr /* @noflip */',
    display: { xs: 'flex', sm: 'block' },
    gap: { xs: '5px', sm: 'unset' },
    color: { xs: 'text.disabled', sm: 'inherit' },
    fontFamily: 'Avenir',
    fontSize: { xs: '12px', md: '16px' },
    fontWeight: 400,
    paddingLeft: {
      xs: '0px',
      sm: '10px',
    },
  },
  secondDate: {
    textAlign: { xs: 'left', sm: 'center' },
    display: { xs: 'flex', sm: 'block' },
    marginLeft: { xs: '0px', sm: '8px' },
    fontSize: { xs: '12px !important', md: '16px !important' },
  },
  cardHeaderContainer: {
    display: { xs: 'none', sm: 'grid' },
    gridTemplateColumns: { xs: '1fr', sm: '1.5fr 3.5fr 1.5fr' },
    p: { xs: '0px 40px 0px 40px', sm: '0px 40px 0 90px' },
    margin: '15px 0px 13px',
    gap: { xs: '40px', sm: '10px' },
    '& > span': {
      color: 'icon.secondary',
      fontfamily: 'Avenir',
      fontsize: '14px !important',
      fontweight: '500',
    },
  },
  cardDate: {
    textAlign: { xs: 'left', sm: 'center' },
  },
}
export default NotificationCenterCards
